import { type FC, type ReactElement, useId } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"

import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"

import { VerticalProgressBarStarIcon as Icon } from "./vertical-progress-bar-star.icon"
import { verticalProgressBarConfig as config } from "./vertical-progress-bar.config"
import {
    EVerticalProgressBarAccessibilityStatus as EAccessibilityStatus,
    EVerticalProgressBarColorStatus as EColorStatus,
    EVerticalProgressBarFillLevel as EFillLevel,
    EVerticalProgressBarInteractivityStatus as EInteractivityStatus,
    EVerticalProgressBarStarVisibilityStatus as EStarVisibilityStatus,
    EVerticalProgressBarTooltipPlacement as ETooltipPlacement,
    EVerticalProgressBarTooltipVisibilityStatus as ETooltipVisibilityStatus,
    EVerticalProgressBarVariant as EVariant,
    type TVerticalProgressBarPieceRenderConfig as TPieceRenderConfig
} from "./vertical-progress-bar.types"
import { verticalProgressBarUtils as utils } from "./vertical-progress-bar.utils"

const piecesClasses: {
    firstPiece(config: TPieceRenderConfig): string
    secondPiece(config: TPieceRenderConfig): string
    thirdPiece(config: TPieceRenderConfig): string
    thirdPieceBottom(config: TPieceRenderConfig): string
} = utils.generatePiecesClasses()

type TProps = {
    variant: EVariant
    fillLevel: EFillLevel
    colorStatus?: EColorStatus
    interactivityStatus?: EInteractivityStatus
    accessibilityStatus?: EAccessibilityStatus
    starVisibilityStatus?: EStarVisibilityStatus
    tooltipConfig?: {
        visibility?: ETooltipVisibilityStatus
        placement?: ETooltipPlacement
    }
    wrapperClassName?: string
}

const Component: FC<TProps> = ({
    fillLevel: level = EFillLevel.NotFilled,
    colorStatus: status = EColorStatus.Expired,
    interactivityStatus = EInteractivityStatus.Idle,
    accessibilityStatus = EAccessibilityStatus.Accessible,
    variant = EVariant.Standalone,
    starVisibilityStatus: starStatus = EStarVisibilityStatus.Visible,
    tooltipConfig: {
        visibility: tooltipVisibility = ETooltipVisibilityStatus.Visible,
        placement: tooltipPlacement = ETooltipPlacement.Center
    } = { visibility: ETooltipVisibilityStatus.Visible, placement: ETooltipPlacement.Left },
    wrapperClassName = String()
}: TProps): ReactElement => {
    const barId: string = useId()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isActionAffected: boolean = eq(interactivityStatus, EInteractivityStatus.Active)

    const color: string = config.barFillColorMap[status](isActionAffected)

    return (
        <div
            {...(eq(tooltipVisibility, ETooltipVisibilityStatus.Visible) &&
                eq(accessibilityStatus, EAccessibilityStatus.Accessible) && {
                    "data-for": barId,
                    "data-tip": barId
                })}
            key={`vpb-${barId}`}
            className={utils.getContainerClasses(variant, wrapperClassName)}
        >
            <div
                data-index={1}
                className={piecesClasses.firstPiece({
                    variant,
                    status,
                    isMobile: breakpoints.xs || breakpoints.sm,
                    withoutStar: !eq(starStatus, EStarVisibilityStatus.Visible),
                    isHidden: eq(variant, EVariant.FullBar) ? eq(level, EFillLevel.NotFilled) : false,
                    isFilled: includes<EFillLevel>([EFillLevel.ThreePiecesFilled], level),
                    isAccessible: eq(accessibilityStatus, EAccessibilityStatus.Accessible),
                    isBarFullHeight: eq(variant, EVariant.Standalone),
                    isActionAffected,
                    color
                })}
            >
                {eq(starStatus, EStarVisibilityStatus.Visible) && (
                    <Icon
                        fillColor={utils.getStarFillColor(level)}
                        {...utils.getStarStroke(level)}
                        {...config.iconDimensionsMap[variant]}
                    />
                )}
            </div>

            <div
                data-index={2}
                className={piecesClasses.secondPiece({
                    variant,
                    status,
                    isMobile: breakpoints.xs || breakpoints.sm,
                    isHidden: eq(variant, EVariant.FullBar) ? eq(level, EFillLevel.NotFilled) : false,
                    isFilled: includes<EFillLevel>([EFillLevel.TwoPiecesFilled, EFillLevel.ThreePiecesFilled], level),
                    isAccessible: eq(accessibilityStatus, EAccessibilityStatus.Accessible),
                    isActionAffected,
                    color
                })}
            />

            <div
                data-index={3}
                className={piecesClasses.thirdPiece({
                    variant,
                    status,
                    isMobile: breakpoints.xs || breakpoints.sm,
                    isHidden: eq(variant, EVariant.FullBar) ? eq(level, EFillLevel.NotFilled) : false,
                    isFilled: !eq(level, EFillLevel.NotFilled),
                    isAccessible: eq(accessibilityStatus, EAccessibilityStatus.Accessible),
                    isBarFullHeight: eq(variant, EVariant.FullBar),
                    isActionAffected,
                    color
                })}
            >
                <div
                    className={piecesClasses.thirdPieceBottom({
                        variant,
                        status,
                        isMobile: breakpoints.xs || breakpoints.sm,
                        isBottomRounded: true,
                        isHidden: !eq(level, EFillLevel.NotFilled),
                        isFilled: eq(level, EFillLevel.NotFilled),
                        isBarFullHeight: includes<EVariant>([EVariant.FullBar, EVariant.Standalone], variant),
                        isActionAffected,
                        ...(eq(variant, EVariant.Standalone) && { color })
                    })}
                />
            </div>

            {utils.renderTooltip(
                eq(tooltipVisibility, ETooltipVisibilityStatus.Visible) &&
                    eq(accessibilityStatus, EAccessibilityStatus.Accessible)
            )({
                barId,
                barLevel: level,
                placement: tooltipPlacement
            })}
        </div>
    )
}

Component.displayName = "VerticalProgressBar"

export { Component as VerticalProgressBar, type TProps as TProgressBarProps }
